import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import { servicesData } from "@/data/servicesData";
import VideoModal from "@/components/ui/VideoModal";
import { PlayCircleIcon } from "@heroicons/react/24/solid";

/**
 * Features/Services móvil optimizado para iOS 26
 * - Fondo oscuro simple
 * - Mismo diseño que desktop ModernServicesSection
 */
const MobileFeatures: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = `/${i18n.language}`;
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const handleLearnMore = (videoId?: string) => {
    if (videoId) setSelectedVideoId(videoId);
  };

  return (
    <section className="relative min-h-screen py-16">
      {/* Content Overlay */}
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-extrabold mb-6 text-[#00342d]"
            style={{
              fontFamily: "Bruno Ace, cursive",
              letterSpacing: "-0.01em",
            }}
          >
            {t("home.services.title", "Our Services")}
          </h2>

          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t(
              "home.services.subtitle",
              "Comprehensive solutions to enhance your veterinary practice."
            )}
          </p>
        </div>

        {/* Services Grid (mirrors Desktop: large service image + pet badge) */}
        <div className="grid grid-cols-1 gap-6 mb-12">
          {servicesData.map((service, index) => (
            <div
              key={service.id || index}
              className="relative overflow-visible h-full"
            >
              <div className="bg-white rounded-[28px] border border-gray-100 shadow-xl h-full group relative overflow-hidden">
                {/* Top Image: service image with play overlay */}
                <div className="relative h-44 overflow-hidden bg-gray-50">
                  {service.enabled === false ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <span className="text-xl font-bold text-gray-400">
                        {t("home.services.comingSoon", "Coming Soon")}
                      </span>
                    </div>
                  ) : (
                    <>
                      <img
                        src={service.serviceImage || service.imageUrl}
                        alt={t(service.titleKey, "")}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/8 group-hover:bg-black/16 transition-colors duration-300" />

                      {service.videoId && (
                        <button
                          onClick={() => handleLearnMore(service.videoId)}
                          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          aria-label={t(
                            "home.services.playVideo",
                            "Play video"
                          )}
                        >
                          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                            <PlayCircleIcon className="w-8 h-8 text-[#00e9be]" />
                          </div>
                        </button>
                      )}
                    </>
                  )}

                  {/* Pet Icon Badge */}
                  <div className="absolute bottom-3 left-3 w-12 h-12 bg-white rounded-xl shadow-md p-2 flex items-center justify-center z-10">
                    <img
                      src={service.petImage || service.imageUrl}
                      alt={t(service.titleKey, "")}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                <div className="flex flex-col h-full p-6 relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-bold text-[#00e9be] bg-[#e6fffa] px-3 py-1 rounded-full">
                      #{index + 1}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-[#00342d] mb-2">
                    {t(service.titleKey, "")}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow text-base">
                    {t(
                      String(service.descriptionKeys.intro),
                      "Service description"
                    )}
                  </p>

                  {/* Action Button */}
                  {service.videoId && (
                    <div className="flex justify-center mt-auto">
                      <Button
                        onClick={() => handleLearnMore(service.videoId)}
                        className="px-8 py-3 text-sm font-bold w-full bg-[#00342d] text-white hover:bg-[#004d43] transition-colors flex items-center justify-center gap-2"
                      >
                        <PlayCircleIcon className="w-5 h-5 text-[#00e9be]" />
                        {t("home.services.learnMore", "Learn More")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate(`${langPrefix}/services`)}
            className="px-8 py-4 text-base font-bold bg-transparent border-2 border-[#00342d] text-[#00342d] hover:bg-[#00342d] hover:text-white transition-all"
          >
            {t("home.services.viewAllButton", "View All Services")}
          </Button>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideoId && (
        <VideoModal
          videoId={selectedVideoId}
          isOpen={!!selectedVideoId}
          onClose={() => setSelectedVideoId(null)}
          title={t("home.services.videoTitle", "Service Video")}
        />
      )}
    </section>
  );
};

export default MobileFeatures;
